import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { clone } from 'lodash';
import Icon from '_Components/Icons/Icon';
import QCheckbox from '_Components/Fields/QCheckbox/QCheckbox';
import EindexRond from '_Charts/KpisCharts/KpiBadges/EindexRond';
import { black, grayShades } from 'styles/abstracts/colors';
import VerbatimListModal from '_Components/Modals/VerbatimListModal/VerbatimListModal';
import { useApolloClient } from 'react-apollo';
import ReactEcharts from 'echarts-for-react';
import { sortByName } from '../../CategoryHierarchyCard';
import '../../../style.scss';
import Loader from '../../../../../../_Components/Loader/OnLoad';
import CModal from '../../../../../../_Components/CModal/CModal';
import NoDataMessage from '../../../../../../_Components/NoDataMessage/NoDataMessage';
import QcardHeader from '../../../../../../_Components/QcardPanel/QcardHeader/QcardHeader';
import GENERATE_IRRITANTS from '../../../graphql/generateIrritants';
import GENERATE_SYNTHESIS from '../../../graphql/generateSynthesis';

const SpanChevron = styled.span`
  width: 1.5rem;
  height: 1.5rem;
  margin-right: 1rem;
  display: inline-block;
`;

const ChevronButton = styled(SpanChevron)`
  border-radius: 0.75rem;
  background-color: ${grayShades.g300};
  text-align: center;
  cursor: pointer;
`;

const StyledCell = styled.td`
  font-weight: bold;
  color: ${black};
  padding: 1.25em 0.75em 0.75em !important;
  padding-left: ${({ level }) => (level === 'subThematic' ? '2em' : level === 'subSubThematic' && '5em')} !important;
`;

const EindexCell = styled(StyledCell)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
  font-weight: bold;
`;

const NumbersCell = styled(StyledCell)`
  color: ${grayShades.g800};
  text-align: center;
`;

const IconCell = styled(StyledCell)`
  color: ${grayShades.g800};
  text-align: center;
`;

const CheckboxCell = styled(StyledCell)`
  margin: auto !important;
  text-align: center;
  width: 2.5rem;
`;

const Checkbox = styled(QCheckbox)`
  margin: 0;
`;

const FlexCell = styled(StyledCell)`
  display: flex;
  color: ${({ isSelected }) => !isSelected && grayShades.g600};
  height: 3.5em !important;
`;

const ModalLink = styled.span`
  cursor: ${({ isCategorySelected }) => isCategorySelected && 'pointer'};
`;

const SynthesisTreeChart = ({ data = [] }) => {
  const COLORS = ['#491870', '#f9b566', '#f9d966', '#2b044a'];

  const dataToTreeChartData = (data, level = 0) => {
    let result = {};
    if (_.has(data, 'children')) {
      result.children = _.map(data.children, child => dataToTreeChartData(child, level + 1));
    }

    const symbol = _.isEmpty(data.children) ? 'circle' : 'roundRect';
    const labelStyle = _.isEmpty(data.children) ? { label: { position: 'right' } } : {};

    result = {
      ...data,
      ...result,
      ...labelStyle,
      symbol,
      itemStyle: { borderColor: '#491870', borderWidth: 2, color: _.get(COLORS, level) },
    };

    return result;
  };

  if (_.isEmpty(data)) {
    return <NoDataMessage mainTitle="No result" secondText="Please, select a thematic with more verbatim" />;
  }

  const option = {
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
    },
    series: [
      {
        type: 'tree',
        id: 0,
        name: 'tree1',
        data: [dataToTreeChartData(data)],
        top: '1%',
        left: '7%',
        bottom: '1%',
        right: '20%',
        symbol: 'emptyCircle',
        symbolSize: 15,
        initialTreeDepth: -1,
        animationDuration: 550,
        animationDurationUpdate: 750,
        roam: true,
        edgeShape: 'polyline',
        edgeForkPosition: '50%',
        orient: 'LR',
        label: {
          backgroundColor: '#faf7f7',
          position: 'top',
          rotate: 0,
          fontSize: 15,
          color: '#2e2d2d',
        },
        lineStyle: {
          width: 2,
          curveness: 0.3,
          color: '#ccc',
        },
        emphasis: {
          lineStyle: {
            width: 3,
            color: '#491870',
          },
        },
      },
    ],
  };

  return (
    <ReactEcharts
      option={option}
      className="react_for_echarts"
      style={{
        minHeight: '800px',
        height: '100%',
        width: '100%',
      }}
      theme="QemotionTheme"
    />
  );
};

const InsightsTable = ({ insight, t, data = [] }) => {
  const MIN_TEXT_SIZE = 300;

  const tableStyle = {
    tableContainer: {
      width: '100%',
      overflowX: 'auto',
      maxHeight: '800px', // Adjust the height as needed
      overflowY: 'scroll',
      display: 'block',
    },
    thead: {
      position: 'sticky',
      top: 0,
      backgroundColor: '#f1f1f1', // Optional: to make the header stand out
      zIndex: 1,
    },
    tbody: {
      display: 'block',
      maxHeight: '250px', // Adjust the height as needed
      overflowY: 'auto',
    },
    th: {
      border: '1px solid #FFFFFF',
      textAlign: 'center',
      padding: '8px',
      backgroundColor: insight === 'irritants' ? '#000000' : '#8bdb00',
      color: '#FFFFFF',
    },
    td: { border: '1px solid #FFFFFF', padding: '8px', overflow: 'hidden' },
  };

  const [expandedRows, setExpandedRows] = useState({});
  const [currentVerbatimIndex, setCurrentVerbatimIndex] = useState(0);

  const nextVerbatim = () => {
    let maxVerbatimSize = Math.max(...data.map(item => item.verbatims.length));
    if (maxVerbatimSize < 0) maxVerbatimSize = 0;

    const temp = currentVerbatimIndex + 1;
    if (temp >= maxVerbatimSize) {
      setCurrentVerbatimIndex(0);
    } else {
      setCurrentVerbatimIndex(temp);
    }
  };

  const previousVerbatim = () => {
    const temp = currentVerbatimIndex - 1;
    if (temp <= 0) {
      setCurrentVerbatimIndex(4);
    } else {
      setCurrentVerbatimIndex(temp);
    }
  };

  const toggleExpand = index => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  if (_.isEmpty(data)) {
    return <NoDataMessage mainTitle="No result" secondText="Please, select a thematic with more verbatim" />;
  }

  return (
    <div>
      <style>
        {`
        .even-row {
          background-color: white;
        }
        .odd-row {
          background-color: #f0f0f0;
        }
        .expandable-cell {
          position: relative;
          max-height: 50px; /* Set a fixed height */
          overflow: hidden;
          transition: max-height 0.3s ease;
        }
        .expandable-cell.expanded {
          max-height: 200px; /* Adjust as needed */
        }
        .ellipsis {
          color: #007bff;
          cursor: pointer;
          font-weight: bold;
        }
        .ellipsis:hover {
          text-decoration: underline;
        }
      `}
      </style>
      <table style={{ width: '100%', borderCollapse: 'collapse', ...tableStyle.tableContainer }}>
        <thead style={tableStyle.thead}>
          <tr style={tableStyle.tr}>
            <th style={tableStyle.th}>{t('Subtheme')}</th>
            <th style={tableStyle.th}>
              {insight === 'synthesis' ? 'Synthèse' : insight === 'irritants' ? t('Irritant') : t('Enchantment')}
            </th>
            <th style={tableStyle.th}>{t('Explications')}</th>
            <th style={tableStyle.th}>{t('Frequency')}</th>
            <th style={tableStyle.th}>
              <ChevronButton onClick={() => previousVerbatim()}>
                <Icon icon="INTERFACE_CHEVRON_LEFT" color={grayShades.g800} />
              </ChevronButton>
              <span style={{ marginRight: '1em' }}>{t('Verbatims')}</span>
              <ChevronButton onClick={() => nextVerbatim()}>
                <Icon icon="INTERFACE_CHEVRON_RIGHT" color={grayShades.g800} />
              </ChevronButton>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => {
            const verbatimsSize = _.size(item.verbatims);
            const rowIndex = currentVerbatimIndex + 1 > verbatimsSize ? verbatimsSize - 1 : currentVerbatimIndex;
            const verbatimText = _.get(item.verbatims, rowIndex)?.text;

            return (
              <tr key={item.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                <td style={tableStyle.td} className="expandable-cell">
                  {expandedRows[index] ? item.subThematic : `${item.subThematic.slice(0, MIN_TEXT_SIZE)}`}
                  {item.subThematic.length > MIN_TEXT_SIZE && (
                    <span className="ellipsis" onClick={() => toggleExpand(index)}>
                      {expandedRows[index] ? 'Voir moins' : '...Voir plus'}
                    </span>
                  )}
                </td>
                <td style={tableStyle.td} className="expandable-cell">
                  {expandedRows[index] ? item.title : `${item.title.slice(0, MIN_TEXT_SIZE)}`}
                  {item.title.length > MIN_TEXT_SIZE && (
                    <span className="ellipsis" onClick={() => toggleExpand(index)}>
                      {expandedRows[index] ? 'Voir moins' : '...Voir plus'}
                    </span>
                  )}
                </td>

                <td style={tableStyle.td} className="expandable-cell">
                  {expandedRows[index] ? item.explainSummary : `${item.explainSummary.slice(0, MIN_TEXT_SIZE)}`}
                  {item.explainSummary.length > MIN_TEXT_SIZE && (
                    <span className="ellipsis" onClick={() => toggleExpand(index)}>
                      {expandedRows[index] ? 'Voir moins' : '...Voir plus'}
                    </span>
                  )}
                </td>
                <td style={{ ...tableStyle.td, minWidth: '14%' }}>
                  {_.times(item.proportion).map(_i => (
                    <span>
                      <Icon icon="PICTO_STAR_BLACK" color="#592EA0" size={24} />
                    </span>
                  ))}
                  {_.times(5 - item.proportion).map(_i => (
                    <span>
                      <Icon icon="PICTO_STAR_WHITE" color="#592EA0" size={24} />
                    </span>
                  ))}
                </td>
                <td style={{ ...tableStyle.td, minWidth: '25%' }} className="expandable-cell">
                  <span>
                    <Icon icon="PICTO_PERSON" color="#592EA0" />
                    <Icon icon="PICTO_MESSAGE" color="#592EA0" size={48} />
                  </span>

                  <span>{`${rowIndex + 1} / ${verbatimsSize}`}</span>
                  <p>
                    <i>
                      {expandedRows[index] ? verbatimText : `${verbatimText?.slice(0, MIN_TEXT_SIZE)}`}
                      {verbatimText?.length > MIN_TEXT_SIZE && (
                        <span className="ellipsis" onClick={() => toggleExpand(index)}>
                          {expandedRows[index] ? 'Voir moins' : '...Voir plus'}
                        </span>
                      )}
                    </i>
                  </p>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AIModal = ({ isOpen, thematicAnalyzed, insight, icon, iconColor, generationText, t, ...props }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const client = useApolloClient();

  const fetchSynthesis = async params => {
    const {
      projectId,
      range,
      thematics,
      selectedEmotions,
      selectedClusters,
      criteriaKeys,
      dataCollectorId,
      keywords,
    } = params;

    const fetchResponse = await client.query({
      query: GENERATE_SYNTHESIS,
      variables: {
        projectId,
        range,
        criteriaKeys,
        thematics,
        dataCollectorId,
        keywords,
        clusters: selectedClusters,
        emotions: selectedEmotions,
        thematicAnalyzed: {
          thematic: thematicAnalyzed.thematic,
          subThematic: thematicAnalyzed.subthematic,
          subSubThematic: thematicAnalyzed.subsubthematic,
        },
      },
    });
    setData(fetchResponse?.data?.generateSynthesis);
    setLoading(false);
  };

  const fetchInsights = async params => {
    const { projectId, range, thematics, selectedEmotions, criteriaKeys, dataCollectorId, keywords } = params;

    const fetchResponse = await client.query({
      query: GENERATE_IRRITANTS,
      variables: {
        projectId,
        range,
        criteriaKeys,
        thematics,
        dataCollectorId,
        emotions: selectedEmotions,
        keywords,
        thematicAnalyzed: {
          thematic: thematicAnalyzed.thematic,
          subThematic: thematicAnalyzed.subthematic,
          subSubThematic: thematicAnalyzed.subsubthematic,
        },
        insight,
      },
    });
    setData(fetchResponse?.data?.generateIrritants);
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen && !loading) {
      const params = { ...props.filters, insight, thematicAnalyzed };
      setLoading(true);
      if (insight === 'irritants' || insight === 'enchantements') {
        fetchInsights(params);
      } else if (insight === 'synthesis') {
        fetchSynthesis(params);
      }
    }
  }, [isOpen, props.filters]);

  const customHeader = (
    <div>
      <div style={{ display: 'inline-block', position: 'absolute', top: '16px', left: '24px' }}>
        <img
          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAADIElEQVR4nO1YTWtTQRSdIvhRFesXWAuCgpuCCIILa2ptmryZV2MbKmntyn0hNu9OgsVNduq+/ooutCtBLLyZebEgBKTqyv6F6i+wRG7bpM/0tXkfk5DIO3Ah0MzknNt775wZQmLEiOELJSpGOVObGMCcBOk1AJUbnKkaBn4mvQRuymSdfD0sQ4yTXgEwtdosgFP5nvQCwBBpoHL7oAD1p0idFOlWWIYYxyx7kt/vhW38TleVU4mKUXfD+g1cg2t9/UZK3ARTZcFQmZe0OqhVwN6orIWMzVb7Lz1yznOmngJV8xhFo/Ikzz6c0CYAmPoZVgCubbW/ZVTG6+RhLwpU3tEowEmELaFWB1yBysFm8oBhyLnSyOpZ0t1NXOuzDMU8BdAdESOkHcBRiSPTa4wCUxN+9ylMfLpxKHmKYc9Dxr7UFhGYZY/sv/O7PpdbOVagcvpoAWo+SEKCl9OB/4Dz0O96ztRwS/J0N6zk2lBXmbml7JeLnDqzfgWAqbLlTLVfvwDmJILYaSwbzHwg8nQ3OBUzPC2vY+OTzqPW9zzlXOPMeRyUODRFiVUmca+OUS9O2leOHJUhg6dlEk/wthF/kaqe45PygW7i0DRmi6a6vzBmn9FKHo2Z29u0OzgVs3iiaxMAhp3tFHloiPA/wn0IUJlOCwCdjwqlVOUqWuLOCRBTi9P2ANGJcu7HcctwboeZ+eA/6znLFLfwfNFK/h8hmWo/Z+Ku1qY25Rzu+WzMPkk6BctYv8DNz0kdY7MU9p6QZ+oyMPWdU/UNP4cSklwb2rn7Bp0ypjAxCSQKgMo3Lu//OlJZUTEThPxKrhatzq3c+ilgasvlPn9HcYhozPyWjRU180ieM/XW48K+jH8LtyteJ+3WHsmIcK3EOsdScWfeQ8QWfidMTxx6oacN8nPlKD5np2EDvECE+Q2vJxXQ9bSC06bdAhanvw54nRFFHY9b7S6hhggq7h2YPEwNE13Ym0DLept4Hzz98XSBCpflEFPa7UHzGOVM/tJ50caGRksMzEloN2Z1YKm4sv+K9BryaCXwvZPKjSg1HyNGjBj/N/4CHLpRmzINzWUAAAAASUVORK5CYII="
          alt="sparkling--v1"
        />
      </div>
      <div style={{ display: 'inline-block', position: 'absolute', top: '16px', right: '24px' }}>
        <Icon icon={icon} size={48} color={iconColor} />
        <b style={{ position: 'relative', top: '-1.3em', fontSize: '-1.5em' }}>{t('AI')}</b>
      </div>
      <QcardHeader {...props} />
    </div>
  );

  return (
    <CModal {...props} size="xl" customHeader={customHeader}>
      {loading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <div>
            <Icon icon={icon} size={48} color={iconColor} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <p style={{ textAlign: 'center' }}>
              <h2>{generationText}</h2>
            </p>
            <p style={{ textAlign: 'center' }}>{t('Ai analysis explain')}</p>

            <Loader icon="INTERFACE_LOADING" />
            <p style={{ textAlign: 'center', width: '100%' }}>{t('Loading')}</p>
          </div>
        </div>
      ) : insight === 'synthesis' ? (
        <SynthesisTreeChart data={data} />
      ) : (
        <InsightsTable data={data} insight={insight} t={t} />
      )}
    </CModal>
  );
};

const TableRow = ({
  data,
  level,
  showSelection,
  isGlobalSelected,
  setGlobalSelected,
  categories,
  setCategories,
  thematicIndex,
  settings,
  index,
  parentObject,
  selectedThematics,
  setSelectedThematics,
  filterKeys,
  displayAll,
  setDisplayAll,
  selectedThematicSelectionOption,
  isAiAvailable,
  setTargetId,
  setTooltipOpen,
  t,
  verbatimPercentage,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [category, setCategory] = useState(data);
  const [isCategorySelected, setIsCategorySelected] = useState(data.thematicsOverviewDetails?.isSelected);
  const [modal, setModal] = useState(false);
  const [irritantsModal, setIrritantsModal] = useState(false);
  const [enchantementsModal, setEnchantementsModal] = useState(false);
  const [synthesisModal, setSynthesisModal] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(selectedThematics);

  useEffect(() => {
    setCategory(data);
  }, [data, isGlobalSelected]);

  useEffect(() => {
    setIsCategorySelected(category.thematicsOverviewDetails?.isSelected);
  }, [data, category, isGlobalSelected, selectedThematicSelectionOption, displayAll]);

  useEffect(() => {
    if (!showSelection) {
      setCategory(data);
      setIsCategorySelected(data?.thematicsOverviewDetails?.isSelected);
    }
  }, [showSelection]);

  useEffect(() => {
    setExpanded(displayAll);
  }, [displayAll]);

  const getCurrentItem = () => {
    let item = {};
    switch (level) {
      case 'thematic':
        item = categories[thematicIndex];
        break;
      case 'subThematic':
        item = categories[thematicIndex]?.subThematics[index];
        break;
      case 'subSubThematic':
        item = parentObject?.subSubThematics?.[index];
    }
    return item;
  };

  const getHierarchy = () => {
    let item = {};
    switch (level) {
      case 'thematic':
        item = {
          thematic: categories[thematicIndex]?.thematicsOverviewDetails?.name,
          subthematic: null,
          subsubthematic: null,
        };
        break;
      case 'subThematic':
        item = {
          thematic: categories[thematicIndex]?.thematicsOverviewDetails?.name,
          subthematic: data?.thematicsOverviewDetails?.name,
          subsubthematic: null,
        };
        break;
      case 'subSubThematic':
        item = {
          thematic: categories[thematicIndex]?.thematicsOverviewDetails?.name,
          subthematic: parentObject?.thematicsOverviewDetails?.name,
          subsubthematic: data?.thematicsOverviewDetails?.name,
        };
    }
    return item;
  };

  useEffect(() => {
    const currentItem = getCurrentItem();
    const modifiedCategories = clone(categories);
    if (level === 'thematic') {
      modifiedCategories[thematicIndex] = currentItem;
    } else if (level === 'subThematic') {
      modifiedCategories[thematicIndex].subThematics[index] = currentItem;
    }
    setCategories(modifiedCategories);
  }, [isCategorySelected]);

  useEffect(() => {
    const lookupValue = JSON.stringify(getHierarchy());
    const categorySelected = selectedCategories?.some(thematic => JSON.stringify(thematic) === lookupValue);
    setIsCategorySelected(categorySelected);
  }, [isGlobalSelected, data, category, selectedThematicSelectionOption, displayAll, selectedCategories, selectedThematics]);

  useEffect(() => {
    setSelectedCategories([]);
    if (selectedThematics && selectedThematics?.length > 0) {
      setSelectedCategories(selectedThematics);
    }
  }, [selectedThematics]);

  const { thematicsOverviewDetails, subThematics, subSubThematics } = category;

  const { thematic, subthematic, subsubthematic } = getHierarchy();

  const categoryHierarchy = {
    thematic,
    subThematic: subthematic,
    subSubThematic: subsubthematic,
    joinSpecify: 'and',
  };

  const propsModal = {
    numberOfMentions: thematicsOverviewDetails.mentionCount,
    title: `${categoryHierarchy.thematic} ${
      categoryHierarchy.subThematic ? `> ${categoryHierarchy.subThematic}` : ''
    } ${categoryHierarchy.subSubThematic ? `> ${categoryHierarchy.subSubThematic}` : ''}`,
    titleSecondPart: '',
    toggle: () => setModal(!modal),
    className: '',
    modal,
  };

  const irritantsModalProps = {
    ...propsModal,
    toggle: () => setIrritantsModal(!irritantsModal),
    modal: irritantsModal,
  };

  const enchantementsModalProps = {
    ...propsModal,
    toggle: () => setEnchantementsModal(!enchantementsModal),
    modal: enchantementsModal,
  };

  const synthesisModalProps = {
    ...propsModal,
    toggle: () => setSynthesisModal(!synthesisModal),
    modal: synthesisModal,
  };

  const targetId = `tooltip-${data.thematicsOverviewDetails.name.replace(/\W/g, '_')}`;

  const handleMouseEnter = id => {
    setTargetId(id);
    setTooltipOpen(true);
  };
  return (
    thematicsOverviewDetails && (
      <>
        <tr>
          {showSelection && (
            <CheckboxCell>
              <div
                style={{
                  height: '3em',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Checkbox
                  selected={isCategorySelected}
                  onClick={() => {
                    setIsCategorySelected(!isCategorySelected);
                    if (isCategorySelected) {
                      setSelectedThematics(
                        selectedCategories.filter(cat => JSON.stringify(cat) !== JSON.stringify(getHierarchy())),
                      );
                    } else {
                      setSelectedThematics([...selectedCategories, getHierarchy()]);
                    }
                  }}
                />
              </div>
            </CheckboxCell>
          )}
          <FlexCell level={level} isSelected={isCategorySelected}>
            <div
              style={{
                height: '3em',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {(level === 'subThematic' && subSubThematics && subSubThematics[0]) ||
              (level === 'thematic' && subThematics && subThematics[0]) ? (
                <ChevronButton onClick={() => setExpanded(!expanded)}>
                  <Icon
                    icon={expanded ? 'INTERFACE_CHEVRON_DOWN' : 'INTERFACE_CHEVRON_RIGHT'}
                    color={grayShades.g800}
                  />
                </ChevronButton>
              ) : (
                <div style={{ width: '2.5em', height: '1.5em' }} />
              )}
              <ModalLink isCategorySelected={isCategorySelected} onClick={() => isCategorySelected && setModal(!modal)}>
                {thematicsOverviewDetails.name}
              </ModalLink>
              <VerbatimListModal
                {...propsModal}
                filters={{
                  ...filterKeys,
                  thematics: [...filterKeys.thematics, categoryHierarchy],
                  sort: 'DESC',
                  selectedEmotions: null,
                }}
                mentionCount={thematicsOverviewDetails?.mentionCount}
                classBody="containBubble"
              />
            </div>
          </FlexCell>
          {isCategorySelected && (
            <>
              <NumbersCell>{thematicsOverviewDetails.mentionCount}</NumbersCell>
              <NumbersCell>{verbatimPercentage}</NumbersCell>
              {thematicsOverviewDetails.eindex !== null ? (
                <EindexCell>
                  <EindexRond type="xs" eindex={thematicsOverviewDetails.eindex} />
                </EindexCell>
              ) : (
                <NumbersCell>N/A</NumbersCell>
              )}
              <IconCell />

              <IconCell>
                <ModalLink
                  id={`${targetId}_synthesis`}
                  onMouseEnter={() => handleMouseEnter(`${targetId}_synthesis`)}
                  isCategorySelected={isCategorySelected && isAiAvailable}
                  onClick={() => isCategorySelected && isAiAvailable && setSynthesisModal(!synthesisModal)}
                >
                  <Icon className={!isAiAvailable && 'disabledIcon'} icon="SYNTHESIS" size={28} color="#000" />
                </ModalLink>

                <AIModal
                  t={t}
                  icon="SYNTHESIS"
                  iconColor="#000"
                  generationText={t('Generating synthesis')}
                  thematicAnalyzed={getHierarchy()}
                  insight="synthesis"
                  isOpen={synthesisModal}
                  {...synthesisModalProps}
                  filters={{
                    ...filterKeys,
                    thematics: [...filterKeys.thematics, categoryHierarchy],
                    sort: 'DESC',
                    selectedEmotions: null,
                  }}
                />
              </IconCell>
              <IconCell>
                <ModalLink
                  id={`${targetId}_enchantements`}
                  onMouseEnter={() => handleMouseEnter(`${targetId}_enchantements`)}
                  isCategorySelected={isCategorySelected && isAiAvailable}
                  onClick={() => isCategorySelected && isAiAvailable && setEnchantementsModal(!enchantementsModal)}
                >
                  <Icon className={!isAiAvailable && 'disabledIcon'} icon="SUN" size={28} color="#8bdb00" />
                </ModalLink>
                <AIModal
                  t={t}
                  icon="SUN"
                  iconColor="#8bdb00"
                  generationText={t('Generating enchantments')}
                  thematicAnalyzed={getHierarchy()}
                  insight="enchantements"
                  isOpen={enchantementsModal}
                  {...enchantementsModalProps}
                  filters={{
                    ...filterKeys,
                    thematics: [...filterKeys.thematics, categoryHierarchy],
                    sort: 'DESC',
                    selectedEmotions: null,
                  }}
                />
              </IconCell>
              <IconCell>
                <ModalLink
                  id={`${targetId}_irritants`}
                  onMouseEnter={() => handleMouseEnter(`${targetId}_irritants`)}
                  isCategorySelected={isCategorySelected && isAiAvailable}
                  onClick={() => isCategorySelected && isAiAvailable && setIrritantsModal(!irritantsModal)}
                >
                  <Icon
                    className={!isAiAvailable && 'disabledIcon'}
                    icon="INTERFACE_IRRITANTS"
                    size={28}
                    color="#000"
                  />
                </ModalLink>
                <AIModal
                  t={t}
                  icon="INTERFACE_IRRITANTS"
                  iconColor="#000"
                  generationText={t('Generating irritants')}
                  thematicAnalyzed={getHierarchy()}
                  insight="irritants"
                  isOpen={irritantsModal}
                  {...irritantsModalProps}
                  filters={{
                    ...filterKeys,
                    thematics: [...filterKeys.thematics, categoryHierarchy],
                    sort: 'DESC',
                    selectedEmotions: null,
                  }}
                />
              </IconCell>
            </>
          )}
        </tr>
        {(subThematics || subSubThematics) &&
          sortByName(subThematics || subSubThematics).map(
            (child, index) =>
              expanded && (
                <TableRow
                  data={child}
                  showSelection={showSelection}
                  level={child.subSubThematics !== undefined ? 'subThematic' : 'subSubThematic'}
                  isGlobalSelected={isGlobalSelected}
                  setGlobalSelected={setGlobalSelected}
                  categories={categories}
                  setCategories={setCategories}
                  thematicIndex={thematicIndex}
                  index={index}
                  parentObject={
                    child.subSubThematics === undefined &&
                    categories[thematicIndex].subThematics.find(item => item.subSubThematics === subSubThematics)
                  }
                  selectedThematics={selectedThematics}
                  setSelectedThematics={setSelectedThematics}
                  settings={settings}
                  filterKeys={filterKeys}
                  displayAll={displayAll}
                  setDisplayAll={setDisplayAll}
                  selectedThematicSelectionOption={selectedThematicSelectionOption}
                  isAiAvailable={isAiAvailable}
                  setTargetId={setTargetId}
                  setTooltipOpen={setTooltipOpen}
                  t={t}
                  verbatimPercentage={verbatimPercentage}
                />
              ),
          )}
      </>
    )
  );
};

export default TableRow;
